import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';

import { useAuth } from '../contexts/AuthContext';
import { ApiError, type FieldIssue } from '../services/api/client';

interface LocationState {
  /** Convite da turma (PRD 33.1) — sem ele não há como criar conta. */
  token?: string;
  from?: string;
}

/**
 * Criar conta.
 *
 * Não existe mais cadastro público (PRD 33.1): só chega aqui quem veio de um
 * QR Code/link de matrícula, carregando o token pelo `state` da navegação
 * (nunca pela URL — o token não deve aparecer em histórico/logs de acesso).
 * Sem token, mostramos a orientação de procurar o professor em vez do
 * formulário.
 */
export default function RegisterPage() {
  const { user, loading, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const state = (location.state as LocationState | null) ?? {};
  const inviteToken = state.token;
  const destination = state.from ?? '/';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [issues, setIssues] = useState<FieldIssue[]>([]);
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="centered-state">
        <p>Carregando…</p>
      </div>
    );
  }

  if (user) {
    return <Navigate to={destination} replace />;
  }

  if (!inviteToken) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <p className="eyebrow">Gestão de Processos</p>
          <h1>Criar conta de aluno</h1>
          <p className="muted">
            Não existe cadastro aberto. Para criar sua conta, use o QR Code ou o link de
            matrícula que o professor forneceu.
          </p>
          <p className="auth-switch">
            Já tem conta?{' '}
            <Link to="/login" state={{ from: destination }}>
              Entrar
            </Link>
          </p>
        </div>
      </div>
    );
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setIssues([]);
    setSubmitting(true);

    try {
      const classId = await register(name, email, password, inviteToken!);
      void navigate(`/turmas/${classId}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setIssues(err.issues ?? []);
      } else {
        setError('Não foi possível criar a conta. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">Gestão de Processos</p>
        <h1>Criar conta de aluno</h1>

        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="name">Nome completo</label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <label htmlFor="email">E-mail</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label htmlFor="password">Senha</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="field-hint">Mínimo de 8 caracteres.</p>

          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          {issues.length > 0 && (
            <ul className="form-issues">
              {issues.map((issue) => (
                <li key={issue.field}>{issue.message}</li>
              ))}
            </ul>
          )}

          <button type="submit" className="primary" disabled={submitting}>
            {submitting ? 'Criando…' : 'Criar conta'}
          </button>
        </form>

        <p className="auth-switch">
          Já tem conta?{' '}
          <Link to="/login" state={{ from: destination }}>
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
}
